import { Application, Graphics, Text, Assets } from 'pixi.js';
import { ComponentStore, World, type System } from '@ecs/world';
import type { GameScene } from '@game/types';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import { createInputSystem } from '@game/systems/input/createInputSystem';
import { createTimeScaleSystem } from '@game/systems/time/createTimeScaleSystem';
import { createGraphicsSyncSystem } from '@game/systems/render/createGraphicsSyncSystem';
import { createSpineSyncSystem } from '@game/systems/render/createSpineSyncSystem';
import {
	createDebugColliderSyncSystem,
	type DebugColliderStyle
} from '@game/systems/render/createDebugColliderSyncSystem';
import { createEnemySystem } from '@game/systems/combat/createEnemySystem';
import { createProjectileSystem } from '@game/systems/combat/createProjectileSystem';
import { createMovementSystem } from '@game/systems/player/createMovementSystem';
import { createAmmoSystem as createAmmoSystemSys } from '@game/systems/combat/createAmmoSystem';
import { createMeleeSystem as createMeleeSystemSys } from '@game/systems/combat/createMeleeSystem';
import { createExplosionSystem as createExplosionSystemSys } from '@game/systems/combat/createExplosionSystem';
import { createSpineAnimationSystem as createSpineAnimationSystemSys } from '@game/systems/player/createSpineAnimationSystem';
import { createPlatformGraphicsSyncSystem as createPlatformGraphicsSyncSystemSys } from '@game/systems/render/createPlatformGraphicsSyncSystem';
import { createAmmoGraphicsSyncSystem as createAmmoGraphicsSyncSystemSys } from '@game/systems/ui/createAmmoGraphicsSyncSystem';
import { createDashCooldownGraphicsSyncSystem as createDashCooldownGraphicsSyncSystemSys } from '@game/systems/ui/createDashCooldownGraphicsSyncSystem';
import { createDeathMarkerSystem as createDeathMarkerSystemSys } from '@game/systems/ui/createDeathMarkerSystem';
import { clamp, randomInRange } from '@game/scenes/characterPrototype/math';
import { destroyAllGraphics as destroyAllGraphicsUtil } from '@game/scenes/characterPrototype/render/destroyAllGraphics';
import { getAndIncrementAttemptCounter as getAndIncrementAttemptCounterUtil } from '@game/scenes/characterPrototype/persistence/attemptCounter';
import {
	saveDeathMarker as saveDeathMarkerUtil,
	loadDeathMarkers as loadDeathMarkersUtil
} from '@game/scenes/characterPrototype/persistence/deathMarkersStorage';
import { handlePlayerFire as handlePlayerFireUtil } from '@game/scenes/characterPrototype/combat/playerFire';
import { createPlayer as createPlayerUtil } from '@game/scenes/characterPrototype/player/createPlayer';
import { createCharacterPrototypeFactories } from '@game/scenes/characterPrototype/factories';
import {
	spawnInitialPlatforms as spawnInitialPlatformsUtil,
	spawnInitialEnemies as spawnInitialEnemiesUtil
} from '@game/scenes/characterPrototype/world/initialSpawn';
import {
	handlePlatformGeneration as handlePlatformGenerationUtil,
	handleEnemyGeneration as handleEnemyGenerationUtil,
	handlePlatformCleanup as handlePlatformCleanupUtil,
	handleEnemyCleanup as handleEnemyCleanupUtil,
	handleProjectileCleanup as handleProjectileCleanupUtil
} from '@game/scenes/characterPrototype/world/streaming';
import {
	updatePlatformGeometry as updatePlatformGeometryUtil,
	applyPlatformTuningToExisting as applyPlatformTuningToExistingUtil,
	applyEnemyTuningToExisting as applyEnemyTuningToExistingUtil,
	applyProjectileTuningToExisting as applyProjectileTuningToExistingUtil,
	applyAmmoTuningToExisting as applyAmmoTuningToExistingUtil
} from '@game/scenes/characterPrototype/tuning/applyTuning';
import { spawnDeathMarker as spawnDeathMarkerUtil } from '@game/scenes/characterPrototype/ui/spawnDeathMarker';
import type {
	AmmoComponent,
	CameraResource,
	DeathMarker,
	DeathMarkerComponent,
	DebugControlsResource,
	EnemyComponent,
	ExplosionComponent,
	InputResource,
	KinematicComponent,
	PlatformComponent,
	PlatformResource,
	PlayerHealth,
	PlayerState,
	ProjectileComponent,
	SpineComponent,
	StageBounds,
	TimeScaleResource,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';

const INPUT_RESOURCE = 'joystick-input';
const BOUNDS_RESOURCE = 'stage-bounds';
const PLATFORM_RESOURCE = 'platform';
const PLAYER_VISUAL_Y_OFFSET = 50;
// Physics collider is still centered at Transform.y, but can be taller than it is wide.
// Use this to tune how "tall" the player's physical AABB is relative to playerSize.
const PLAYER_COLLIDER_HEIGHT_SCALE = 2;
const DEBUG_PLAYER_COLLIDER_COLOR = 0x22c55e;
const DEBUG_PLAYER_COLLIDER_ALPHA = 0.18;
const DEBUG_PLAYER_COLLIDER_STROKE_COLOR = 0x16a34a;
const DEBUG_PLAYER_COLLIDER_STROKE_ALPHA = 0.85;
const DEBUG_PLAYER_COLLIDER_STROKE_WIDTH = 2;
const PLATFORM_CORNER_RADIUS = 18;
const PLATFORM_COLOR = 0xfef3c7;
const PLATFORM_BORDER_COLOR = 0xf97316;
const PLAYER_PROJECTILE_COLOR = 0x38bdf8;
const PLAYER_PROJECTILE_STROKE = 0xcffafe;
const PLAYER_MAX_HEALTH = 3;
const PLAYER_FIRE_LEAD_MAX_TIME = 1.8;
const AMMO_CLIP_SIZE = 6;
const GAME_OVER_RESOURCE = 'game-over';
const GAME_OVER_REASON_RESOURCE = 'game-over-reason';
const DEATH_MARKERS_STORAGE_KEY = 'super-jump-death-markers';
const ATTEMPT_COUNTER_STORAGE_KEY = 'super-jump-attempt-counter';

const DEBUG_CONTROLS_RESOURCE = 'debug-controls';

export function createCharacterPrototypeScene(): GameScene {
	const world = new World();
	const transforms = new ComponentStore<TransformComponent>(world);
	const velocities = new ComponentStore<VelocityComponent>(world);
	const kinematics = new ComponentStore<KinematicComponent>(world);
	const states = new ComponentStore<PlayerState>(world);
	const platforms = new ComponentStore<PlatformComponent>(world);
	const enemies = new ComponentStore<EnemyComponent>(world);
	const projectiles = new ComponentStore<ProjectileComponent>(world);
	const ammos = new ComponentStore<AmmoComponent>(world);
	const explosions = new ComponentStore<ExplosionComponent>(world);
	const graphics = new ComponentStore<Graphics>(world);
	const debugColliderGraphics = new ComponentStore<Graphics>(world);
	const ammoGraphics = new ComponentStore<Graphics>(world);
	const dashGraphics = new ComponentStore<Graphics>(world);
	const deathMarkers = new ComponentStore<DeathMarkerComponent>(world);
	const deathMarkerGraphics = new ComponentStore<Graphics>(world);
	const deathMarkerTexts = new ComponentStore<Text>(world);
	const spines = new ComponentStore<SpineComponent>(world);

	const joystickInput: InputResource = { x: 0, y: 0 };
	world.setResource<InputResource>(INPUT_RESOURCE, joystickInput);
	const tuning: Tuning = { ...DEFAULT_TUNING };
	world.setResource<Tuning>('tuning', tuning);
	world.setResource<ComponentStore<ExplosionComponent>>('explosions-store', explosions);
	world.setResource<number | null>('base-platform-entity', null);
	world.setResource<PlatformResource>(PLATFORM_RESOURCE, {
		x: Number.NaN,
		y: Number.NaN,
		width: 0,
		height: tuning.platformHeight
	});
	world.setResource<CameraResource>('camera', { y: 0, followThreshold: tuning.cameraFollowThreshold, peakY: 0 });
	world.setResource<number>('platform-next-y', Number.NaN);
	world.setResource<number>('enemy-next-y', Number.NaN);
	world.setResource<TimeScaleResource>('timescale', { value: 1, base: 1, boostRemaining: 0, boostValue: 1 });
	world.setResource<PlayerHealth>('player-health', { current: PLAYER_MAX_HEALTH, max: PLAYER_MAX_HEALTH });
	world.setResource<boolean>(GAME_OVER_RESOURCE, false);
	world.setResource<'fell' | 'dead'>(GAME_OVER_REASON_RESOURCE, 'fell');
	world.setResource<DebugControlsResource>(DEBUG_CONTROLS_RESOURCE, {
		enemiesFrozen: false,
		playerAnimOverride: null,
		playerAnimToken: 0
	});

	let stage: Application['stage'] | null = null;
	let getBounds: (() => StageBounds) | null = null;
	let onGameOver: ((reason: 'fell' | 'dead') => void) | null = null;
	let didNotifyGameOver = false;

	const factories = createCharacterPrototypeFactories({
		keys: {
			health: 'player-health',
			timescale: 'timescale',
			gameOver: GAME_OVER_RESOURCE,
			gameOverReason: GAME_OVER_REASON_RESOURCE
		},
		ammoClipSize: AMMO_CLIP_SIZE,
		projectileDefaults: {
			lifetime: DEFAULT_TUNING.projectileLifetime,
			size: DEFAULT_TUNING.projectileSize,
			fill: 0x991b1b,
			stroke: 0xfee2e2
		},
		explosionStyle: {
			fillColor: 0xf97316,
			fillAlpha: 0.35,
			strokeColor: 0xf59e0b,
			strokeAlpha: 0.7,
			strokeWidth: 2
		},
		styles: {
			platform: {
				cornerRadius: PLATFORM_CORNER_RADIUS,
				fillColor: PLATFORM_COLOR,
				fillAlpha: 0.95,
				strokeColor: PLATFORM_BORDER_COLOR,
				strokeAlpha: 0.85,
				strokeWidth: 3
			},
			ammoBar: {
				playerFill: 0x38bdf8,
				playerStroke: 0xcffafe,
				enemyFill: 0xff3b30,
				enemyStroke: 0xffffff
			},
			dashBar: {
				backgroundColor: 0x0f172a,
				backgroundAlpha: 0.18,
				fillColor: 0xa78bfa,
				fillAlphaReady: 0.9,
				fillAlphaCooldown: 0.65,
				strokeColor: 0xe9d5ff,
				strokeAlpha: 0.35,
				strokeWidth: 1
			}
		}
	});

	const applyPlayerDamage = factories.applyPlayerDamage;
	const applyTimeBoost = factories.applyTimeBoost;
	const spawnPlatform = factories.spawnPlatform;
	const spawnEnemy = factories.spawnEnemy;
	const spawnProjectile = factories.spawnProjectile;
	const spawnExplosion = factories.spawnExplosion;

	world.registerSystem(createInputSystem(INPUT_RESOURCE, kinematics, velocities));
	world.registerSystem(createTimeScaleSystem());
	world.registerSystem(
		createMovementSystem(kinematics, transforms, velocities, states, platforms, {
			boundsKey: BOUNDS_RESOURCE,
			inputKey: INPUT_RESOURCE,
			gameOverKey: GAME_OVER_RESOURCE,
			gameOverReasonKey: GAME_OVER_REASON_RESOURCE,
			playerColliderHeightScale: PLAYER_COLLIDER_HEIGHT_SCALE,
			applyTimeBoost
		})
	);
	world.registerSystem(
		createSpineAnimationSystemSys(spines, states, velocities, { debugControlsKey: DEBUG_CONTROLS_RESOURCE })
	);
	world.registerSystem(createGraphicsSyncSystem(graphics, transforms));
	world.registerSystem(createSpineSyncSystem(spines, transforms, PLAYER_VISUAL_Y_OFFSET));
	const debugColliderStyle: DebugColliderStyle = {
		fillColor: DEBUG_PLAYER_COLLIDER_COLOR,
		fillAlpha: DEBUG_PLAYER_COLLIDER_ALPHA,
		strokeColor: DEBUG_PLAYER_COLLIDER_STROKE_COLOR,
		strokeAlpha: DEBUG_PLAYER_COLLIDER_STROKE_ALPHA,
		strokeWidth: DEBUG_PLAYER_COLLIDER_STROKE_WIDTH
	};
	world.registerSystem(
		createDebugColliderSyncSystem(debugColliderGraphics, transforms, kinematics, debugColliderStyle)
	);
	world.registerSystem(createPlatformGraphicsSyncSystemSys(platforms, graphics, factories.styles.platform));
	world.registerSystem(createAmmoSystemSys(ammos, { ammoClipSize: AMMO_CLIP_SIZE }));
	world.registerSystem(
		createAmmoGraphicsSyncSystemSys(ammos, ammoGraphics, transforms, {
			style: factories.styles.ammoBar,
			yOffsetExtra: 14
		})
	);
	world.registerSystem(
		createDashCooldownGraphicsSyncSystemSys(dashGraphics, states, transforms, {
			style: factories.styles.dashBar,
			yOffsetExtra: 28,
			minWidth: 28
		})
	);
	world.registerSystem(
		createEnemySystem(enemies, transforms, velocities, projectiles, graphics, platforms, ammos, {
			boundsKey: BOUNDS_RESOURCE,
			debugControlsKey: DEBUG_CONTROLS_RESOURCE,
			ammoClipSize: AMMO_CLIP_SIZE,
			spawnProjectile
		})
	);
	world.registerSystem(
		createProjectileSystem(
			projectiles,
			transforms,
			velocities,
			kinematics,
			graphics,
			enemies,
			platforms,
			ammos,
			ammoGraphics,
			{
				spawnPlatform,
				spawnExplosion,
				applyPlayerDamage
			}
		)
	);
	world.registerSystem(
		createMeleeSystemSys(enemies, transforms, graphics, ammoGraphics, ammos, states, { applyTimeBoost })
	);
	world.registerSystem(createExplosionSystemSys(explosions, graphics));
	world.registerSystem(
		createDeathMarkerSystemSys(deathMarkers, transforms, deathMarkerGraphics, deathMarkerTexts, {
			boundsKey: BOUNDS_RESOURCE,
			textMargin: 4,
			textYOffset: 20
		})
	);

	return {
		init(app) {
			stage = app.stage;
			world.setResource<Application['stage']>('stage', stage);
			getBounds = () => ({
				width: app.renderer.width / app.renderer.resolution,
				height: app.renderer.height / app.renderer.resolution
			});
			const bounds = getBounds();
			world.setResource<StageBounds>(BOUNDS_RESOURCE, bounds);
			const platform = world.getResource<PlatformResource>(PLATFORM_RESOURCE);
			if (bounds && platform) {
				updatePlatformGeometryUtil(bounds, platform, tuning);
			}
			const cameraRes = world.getResource<CameraResource>('camera');
			if (cameraRes) {
				cameraRes.followThreshold = tuning.cameraFollowThreshold;
			}

			// Increment attempt counter
			const attemptNumber = getAndIncrementAttemptCounterUtil(ATTEMPT_COUNTER_STORAGE_KEY);

			const playerEntity = createPlayerUtil(
				world,
				transforms,
				velocities,
				kinematics,
				states,
				graphics,
				debugColliderGraphics,
				ammos,
				ammoGraphics,
				dashGraphics,
				stage,
				platforms,
				spines,
				spawnPlatform,
				{
					boundsKey: BOUNDS_RESOURCE,
					platformKey: PLATFORM_RESOURCE,
					playerColliderHeightScale: PLAYER_COLLIDER_HEIGHT_SCALE,
					playerVisualYOffset: PLAYER_VISUAL_Y_OFFSET,
					ammoClipSize: AMMO_CLIP_SIZE
				}
			);
			if (platform && bounds) {
				world.setResource<number>(
					'platform-next-y',
					platform.y - randomInRange(tuning.platformGapMin, tuning.platformGapMax)
				);
				world.setResource<number>(
					'enemy-next-y',
					platform.y - randomInRange(tuning.enemyGapMin, tuning.enemyGapMax)
				);
			}
			if (bounds && platform) {
				spawnInitialPlatformsUtil(world, platforms, graphics, stage, bounds, platform, tuning, spawnPlatform);
				spawnInitialEnemiesUtil(
					world,
					enemies,
					transforms,
					graphics,
					ammoGraphics,
					ammos,
					stage,
					bounds,
					platform,
					spawnEnemy
				);
			}
			world.setResource<number>('player-entity', playerEntity);
			world.setResource<number>('attempt-number', attemptNumber);
			// Save initial player Y position for height calculation
			const playerTransform = transforms.get(playerEntity);
			if (playerTransform) {
				world.setResource<number>('player-initial-y', playerTransform.y);
				// Load death markers after we know initial Y position
				loadDeathMarkersUtil(DEATH_MARKERS_STORAGE_KEY, Boolean(bounds), playerTransform.y, (marker, worldY) => {
					spawnDeathMarkerUtil(
						world,
						deathMarkers,
						transforms,
						deathMarkerGraphics,
						deathMarkerTexts,
						stage,
						marker.x,
						worldY,
						marker.height,
						marker.attemptNumber,
						{
							boundsKey: BOUNDS_RESOURCE,
							textMargin: 4,
							textYOffset: 20,
							heightThreshold: 10
						}
					);
				});
			}
		},
		setEnemiesFrozen(frozen) {
			const dbg = world.getResource<DebugControlsResource>(DEBUG_CONTROLS_RESOURCE);
			if (!dbg) return;
			dbg.enemiesFrozen = frozen;
		},
		setPlayerAnimationOverride(state) {
			const dbg = world.getResource<DebugControlsResource>(DEBUG_CONTROLS_RESOURCE);
			if (!dbg) return;
			dbg.playerAnimOverride = state;
			dbg.playerAnimToken += 1;
		},
		restartPlayerAnimationOverride() {
			const dbg = world.getResource<DebugControlsResource>(DEBUG_CONTROLS_RESOURCE);
			if (!dbg) return;
			if (!dbg.playerAnimOverride) return;
			dbg.playerAnimToken += 1;
		},
		update(delta) {
			const wasGameOver = world.getResource<boolean>(GAME_OVER_RESOURCE) ?? false;
			if (getBounds && stage) {
				const bounds = getBounds();
				world.setResource<StageBounds>(BOUNDS_RESOURCE, bounds);
				const platform = world.getResource<PlatformResource>(PLATFORM_RESOURCE);
				if (platform) {
					updatePlatformGeometryUtil(bounds, platform, tuning);
					const baseId = world.getResource<number | null>('base-platform-entity');
					if (baseId) {
						const base = platforms.get(baseId);
						if (base) {
							base.x = platform.x;
							base.y = platform.y;
							base.width = platform.width;
							base.height = platform.height;
						}
					}
				}
			}
			world.update(delta);
			const isGameOver = world.getResource<boolean>(GAME_OVER_RESOURCE) ?? false;
			if (!didNotifyGameOver && !wasGameOver && isGameOver) {
				didNotifyGameOver = true;
				const reason = world.getResource<'fell' | 'dead'>(GAME_OVER_REASON_RESOURCE) ?? 'fell';
				// Save death marker
				const camera = world.getResource<CameraResource>('camera');
				const bounds = world.getResource<StageBounds>(BOUNDS_RESOURCE);
				const playerId = world.getResource<number>('player-entity');
				const attemptNumber = world.getResource<number>('attempt-number') ?? 1;
				const playerTransform = playerId ? transforms.get(playerId) : null;
				const initialY = world.getResource<number>('player-initial-y');
				if (camera && playerTransform && stage && bounds && initialY !== undefined) {
					// Calculate height from initial player position (higher Y = lower on screen, so we subtract)
					const height = Math.max(0, initialY - playerTransform.y);
					// Determine which side of screen is closer (left or right)
					const distanceToLeft = playerTransform.x;
					const distanceToRight = bounds.width - playerTransform.x;
					const markerX = distanceToLeft < distanceToRight ? 0 : bounds.width; // Place marker at screen edge

					saveDeathMarkerUtil(DEATH_MARKERS_STORAGE_KEY, { height, attemptNumber, x: markerX });
					// Spawn marker in current game at screen edge
					spawnDeathMarkerUtil(
						world,
						deathMarkers,
						transforms,
						deathMarkerGraphics,
						deathMarkerTexts,
						stage,
						markerX,
						playerTransform.y,
						height,
						attemptNumber,
						{
							boundsKey: BOUNDS_RESOURCE,
							textMargin: 4,
							textYOffset: 20,
							heightThreshold: 10
						}
					);
				}
				onGameOver?.(reason);
			}
			const camera = world.getResource<CameraResource>('camera');
			const bounds = world.getResource<StageBounds>(BOUNDS_RESOURCE);
			if (camera && bounds && stage) {
				handlePlatformGenerationUtil(world, platforms, graphics, stage, camera, bounds, tuning, spawnPlatform);
				handlePlatformCleanupUtil(world, platforms, graphics, camera, bounds, tuning);
				handleEnemyGenerationUtil(
					world,
					enemies,
					transforms,
					graphics,
					ammoGraphics,
					ammos,
					stage,
					camera,
					bounds,
					spawnEnemy
				);
				handleEnemyCleanupUtil(world, enemies, transforms, graphics, ammoGraphics, ammos, camera, bounds);
				handleProjectileCleanupUtil(world, projectiles, transforms, graphics, camera, bounds);
			}
		},
		setInputDirection(x, y) {
			const input = world.getResource<InputResource>(INPUT_RESOURCE);
			if (!input) return;
			input.x = x;
			input.y = y;
		},
		setOnGameOver(handler) {
			onGameOver = handler ?? null;
		},
		triggerFire() {
			handlePlayerFireUtil(
				world,
				transforms,
				velocities,
				projectiles,
				graphics,
				enemies,
				ammos,
				spawnProjectile,
				applyTimeBoost,
				{
					boundsKey: BOUNDS_RESOURCE,
					stageKey: 'stage',
					playerFireLeadMaxTime: PLAYER_FIRE_LEAD_MAX_TIME,
					playerProjectileColor: PLAYER_PROJECTILE_COLOR,
					playerProjectileStroke: PLAYER_PROJECTILE_STROKE
				}
			);
		},
		triggerDash(x, y) {
			const playerId = world.getResource<number>('player-entity');
			const state = playerId ? states.get(playerId) : null;
			const velocity = playerId ? velocities.get(playerId) : null;
			if (!playerId || !state || !velocity) return;
			const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
			const isGameOver = world.getResource<boolean>(GAME_OVER_RESOURCE) ?? false;
			if (isGameOver) return;
			if (!tuning.dashEnabled) return;
			if (state.dashCooldown > 0 || state.dashTime > 0) return;
			const len = Math.hypot(x, y);
			if (len <= 1e-4) return;
			const dx = x / len;
			const dy = y / len;
			state.dashDirX = dx;
			state.dashDirY = dy;
			state.dashTime = Math.max(0.01, tuning.dashDuration);
			state.dashCooldown = Math.max(0.01, tuning.dashCooldown);
			applyTimeBoost(world, tuning.timeBoostDashValue ?? tuning.timeBoostFireValue, tuning.timeBoostDashDuration);
			// immediate impulse so it feels snappy
			velocity.x = dx * tuning.dashSpeed;
			velocity.y = dy * tuning.dashSpeed;
		},
		setTuning(patch) {
			Object.assign(tuning, patch);
			// keep health max in sync (do not heal automatically if max changes)
			const health = world.getResource<PlayerHealth>('player-health');
			if (health) {
				const nextMax = Math.max(1, Math.round(tuning.playerMaxHealth));
				const ratio = health.max > 0 ? health.current / health.max : 1;
				health.max = nextMax;
				health.current = Math.max(0, Math.min(nextMax, Math.round(ratio * nextMax)));
			}

			// If platform-related tuning changes, apply it to existing platforms immediately
			const platformKeys: (keyof Tuning)[] = [
				'platformHeight',
				'platformMinWidth',
				'platformMaxWidth',
				'platformMinSideOffset',
				'platformWidthRatio',
				'platformDefaultBottom'
			];
			const touchedPlatforms = platformKeys.some((key) => key in patch);
			if (touchedPlatforms) {
				const bounds = world.getResource<StageBounds>(BOUNDS_RESOURCE);
				if (bounds) {
					applyPlatformTuningToExistingUtil(platforms, bounds, tuning);
				}
			}

			// Propagate camera follow threshold immediately
			if ('cameraFollowThreshold' in patch) {
				const cameraRes = world.getResource<CameraResource>('camera');
				if (cameraRes) cameraRes.followThreshold = tuning.cameraFollowThreshold;
			}

			// If enemy/projectile sizes changed, re-draw existing enemies/projectiles immediately
			const touchedEnemyVisual =
				'enemySize' in patch || 'projectileSize' in patch || 'playerProjectileSize' in patch;
			if (touchedEnemyVisual) {
				const stage = world.getResource<Application['stage']>('stage');
				if (stage) {
					applyEnemyTuningToExistingUtil(enemies, transforms, graphics, tuning);
					applyProjectileTuningToExistingUtil(projectiles, graphics, tuning);
				}
			}

			const touchedAmmo = 'playerReloadDuration' in patch || 'enemyReloadDuration' in patch;
			if (touchedAmmo) {
				const playerId = world.getResource<number>('player-entity');
				applyAmmoTuningToExistingUtil(ammos, playerId ?? null, tuning, AMMO_CLIP_SIZE);
			}

			// If spawn spacing changed, update next spawn cursors so the next generated objects use new params immediately
			const touchedPlatformSpawn =
				'platformGapMin' in patch || 'platformGapMax' in patch || 'cameraSpawnAhead' in patch;
			if (touchedPlatformSpawn) {
				const cameraRes = world.getResource<CameraResource>('camera');
				if (cameraRes) {
					world.setResource<number>('platform-next-y', cameraRes.y - tuning.platformGapMin);
				}
			}
			const touchedEnemySpawn = 'enemyGapMin' in patch || 'enemyGapMax' in patch || 'enemySpawnAhead' in patch;
			if (touchedEnemySpawn) {
				const cameraRes = world.getResource<CameraResource>('camera');
				if (cameraRes) {
					world.setResource<number>('enemy-next-y', cameraRes.y - tuning.enemyGapMin);
				}
			}
		},
		getTuning() {
			return { ...tuning };
		},
		dispose() {
			destroyAllGraphicsUtil(graphics);
			destroyAllGraphicsUtil(ammoGraphics);
			destroyAllGraphicsUtil(dashGraphics);
			destroyAllGraphicsUtil(deathMarkerGraphics);
			deathMarkerTexts.forEach((text) => {
				text.parent?.removeChild(text);
				text.destroy();
			});
			deathMarkerTexts.clear();
			spines.forEach((spineComp) => {
				spineComp.spine.parent?.removeChild(spineComp.spine);
				spineComp.spine.destroy();
			});
			spines.clear();
			debugColliderGraphics.forEach((g) => {
				g.parent?.removeChild(g);
				g.destroy();
			});
			debugColliderGraphics.clear();
			world.dispose();
			stage = null;
			getBounds = null;
		}
	};
}
